import { isBrowser } from '../utils';

type FlightSegment = string | [string, string, string, ...unknown[]];
type FlightRouterState = [
  FlightSegment,
  { [parallelRouterKey: string]: FlightRouterState | undefined } | null | undefined,
  ...unknown[],
];

/**
 * 从 Next.js 运行时还原路由模板名。
 * - Pages Router：`__NEXT_DATA__.page`（如 /task/[session]）
 * - App Router：解析 history.state 中的 FlightRouterState
 * 无法识别时回退 pathname。
 */
export function resolveNextRouteName(pathname?: string): string {
  const path =
    pathname ||
    (isBrowser() ? window.location.pathname : '/') ||
    '/';

  if (!isBrowser()) return path;

  const pagesRoute = readPagesRouterRoute();
  if (pagesRoute) return pagesRoute;

  const tree = readAppRouterTree();
  if (tree) {
    const pattern = flightTreeToRoutePattern(tree);
    if (pattern) return pattern;
  }

  return path;
}

/** 是否看起来像 Next.js App Router 页面（可能尚未写入路由树） */
export function isLikelyNextAppRouter(): boolean {
  if (!isBrowser()) return false;
  if (readPagesRouterRoute()) return false;
  const w = window as Window & { __next_f?: unknown; next?: { router?: unknown } };
  if (typeof w.__next_f !== 'undefined') return true;
  if (window.history?.state && (window.history.state as { __NA?: boolean }).__NA) {
    return true;
  }
  // 有 _next 静态资源且无 __NEXT_DATA__，多为 App Router
  if (
    !document.getElementById('__NEXT_DATA__') &&
    !!document.querySelector('script[src*="/_next/"], link[href*="/_next/"]')
  ) {
    return true;
  }
  return false;
}

/**
 * 等待 Next 路由树就绪后再解析；超时回退 pathname。
 * 用于 snippet 首屏：App Router hydration 可能晚于脚本执行。
 */
export function resolveNextRouteNameWhenReady(
  pathname?: string,
  timeoutMs = 1200
): Promise<string> {
  const path =
    pathname ||
    (isBrowser() ? window.location.pathname : '/') ||
    '/';

  if (!isBrowser()) return Promise.resolve(path);

  const pagesRoute = readPagesRouterRoute();
  if (pagesRoute) return Promise.resolve(pagesRoute);

  const immediate = resolveNextRouteName(path);
  if (immediate !== path || !isLikelyNextAppRouter()) {
    return Promise.resolve(immediate);
  }

  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const name = resolveNextRouteName(path);
      if (name !== path || readAppRouterTree()) {
        resolve(name);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(path);
        return;
      }
      window.setTimeout(tick, 40);
    };
    tick();
  });
}

function readPagesRouterRoute(): string | null {
  try {
    const el = document.getElementById('__NEXT_DATA__');
    if (!el?.textContent) return null;
    const data = JSON.parse(el.textContent) as { page?: unknown };
    return typeof data.page === 'string' && data.page ? data.page : null;
  } catch {
    return null;
  }
}

function readAppRouterTree(): FlightRouterState | null {
  try {
    const state = window.history?.state as
      | { __PRIVATE_NEXTJS_INTERNALS_TREE?: FlightRouterState }
      | null
      | undefined;
    const tree = state?.__PRIVATE_NEXTJS_INTERNALS_TREE;
    return Array.isArray(tree) ? tree : null;
  } catch {
    return null;
  }
}

function flightTreeToRoutePattern(tree: FlightRouterState): string {
  const parts: string[] = [];
  walk(tree, parts);
  return parts.length ? `/${parts.join('/')}` : '/';
}

function walk(tree: FlightRouterState, parts: string[]): void {
  if (!Array.isArray(tree) || tree.length === 0) return;

  const segment = tree[0];
  const part = segmentToPathPart(segment);
  if (part) parts.push(part);

  const parallel = tree[1];
  if (parallel && typeof parallel === 'object') {
    const children = parallel.children;
    if (children) walk(children, parts);
  }
}

function segmentToPathPart(segment: FlightSegment): string | null {
  if (typeof segment === 'string') {
    if (!segment) return null;
    if (isSpecialSegment(segment) || isRouteGroupOrInterception(segment)) return null;
    if (segment.startsWith('@')) return null; // parallel route slot
    return segment;
  }

  if (!Array.isArray(segment) || segment.length < 2) return null;
  const [name, , type] = segment;
  if (!name) return null;

  // Next DynamicParamTypesShort: d/di 动态；c/ci catch-all；oc/oci 可选 catch-all
  if (type === 'c' || type === 'ci') return `[...${name}]`;
  if (type === 'oc' || type === 'oci') return `[[...${name}]]`;
  return `[${name}]`;
}

function isSpecialSegment(segment: string): boolean {
  return (
    segment === '__PAGE__' ||
    segment === '__DEFAULT__' ||
    segment === '__NOT_FOUND__' ||
    segment.startsWith('__')
  );
}

function isRouteGroupOrInterception(segment: string): boolean {
  return segment.startsWith('(') && segment.endsWith(')');
}
